import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Button, TablePagination, CircularProgress, Avatar,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { adminFetch } from '../../lib/adminAuth';

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [emailEdit, setEmailEdit] = useState(null); // { id, email }
  const [emailError, setEmailError] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/admin/vendors?page=${page + 1}&limit=${limit}&search=${encodeURIComponent(search)}`);
      const data = await res.json();
      setVendors(data.vendors || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    await adminFetch(`/api/admin/vendors/${id}/approve`, { method: 'PATCH' });
    load();
  };

  const handleReject = async (id) => {
    await adminFetch(`/api/admin/vendors/${id}/reject`, { method: 'PATCH' });
    load();
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    await adminFetch(`/api/admin/vendors/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus }),
    });
    load();
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    setEmailError('');
    try {
      const res = await adminFetch(`/api/admin/vendors/${emailEdit.id}/email`, {
        method: 'PATCH',
        body: JSON.stringify({ email: emailEdit.email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setEmailError(data.message || 'Failed to update email');
        return;
      }
      setEmailEdit(null);
      load();
    } catch (err) {
      console.error(err);
      setEmailError('Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>Vendors</Typography>
      <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>Manage businesses and approve signups</Typography>

      <TextField
        placeholder="Search by company, name, or email..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        size="small"
        sx={{ mb: 2, width: 360 }}
      />

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f0f0f0', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Business</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Approved</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }}>Joined</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#999', fontSize: 12 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : vendors.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: '#999' }}>No vendors found</TableCell></TableRow>
            ) : (
              vendors.map((v) => (
                <TableRow key={v._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={v.logo} variant="rounded" sx={{ width: 32, height: 32, bgcolor: '#f0f0f0', fontSize: 13 }}>
                        {v.company?.[0]}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v.company}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#999' }}>{v.full_name}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#555' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{v.email}</span>
                      <IconButton
                        size="small"
                        onClick={() => { setEmailEdit({ id: v._id, email: v.email }); setEmailError(''); }}
                        sx={{ color: '#bbb', '&:hover': { color: '#666' } }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{v.category || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={v.approved ? 'Approved' : 'Pending'}
                      size="small"
                      sx={{
                        fontSize: 11,
                        bgcolor: v.approved ? '#e8f5e9' : '#fff3e0',
                        color: v.approved ? '#2e7d32' : '#e65100',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={v.status || 'active'}
                      size="small"
                      onClick={() => toggleStatus(v._id, v.status || 'active')}
                      sx={{
                        fontSize: 11,
                        cursor: 'pointer',
                        bgcolor: v.status === 'suspended' ? '#ffebee' : '#e8f5e9',
                        color: v.status === 'suspended' ? '#c62828' : '#2e7d32',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{formatDate(v.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {!v.approved ? (
                        <Button
                          size="small"
                          startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleApprove(v._id)}
                          sx={{
                            textTransform: 'none', fontSize: 12, color: '#fff', bgcolor: '#4caf50',
                            borderRadius: 1.5, px: 1.5, py: 0.3,
                            '&:hover': { bgcolor: '#43a047' },
                          }}
                        >
                          Approve
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleReject(v._id)}
                          sx={{
                            textTransform: 'none', fontSize: 12, color: '#999', border: '1px solid #e0e0e0',
                            borderRadius: 1.5, px: 1.5, py: 0.3,
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          rowsPerPageOptions={[limit]}
          sx={{ borderTop: '1px solid #f0f0f0' }}
        />
      </TableContainer>

      <Dialog open={!!emailEdit} onClose={() => !savingEmail && setEmailEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>Change vendor email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email"
            value={emailEdit?.email || ''}
            onChange={(e) => setEmailEdit((s) => ({ ...s, email: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') saveEmail(); }}
            error={!!emailError}
            helperText={emailError || 'The vendor will use this email to sign in.'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailEdit(null)} disabled={savingEmail} sx={{ textTransform: 'none', color: '#999' }}>
            Cancel
          </Button>
          <Button
            onClick={saveEmail}
            disabled={savingEmail}
            variant="contained"
            sx={{ textTransform: 'none', bgcolor: '#4caf50', '&:hover': { bgcolor: '#43a047' } }}
          >
            {savingEmail ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
